import { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

export default function FavoriteButton({ 
  itemType, 
  itemId, 
  itemTitle, 
  itemSource, 
  itemData = null,
  tags = [],
  size = "sm",
  className = "",
  showText = false,
  onToggle = null
}) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkFavoriteStatus();
  }, [itemId, itemType, itemSource]);

  const checkFavoriteStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API}/api/favorites/check`, {
        params: {
          item_type: itemType,
          item_id: itemId,
          item_source: itemSource
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error("Failed to check favorite status:", error);
    }
  };

  const toggleFavorite = async (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Please login to use favorites");
      return;
    }

    setLoading(true);
    
    try {
      if (isFavorite) {
        // Remove from favorites
        await axios.delete(`${API}/api/favorites`, {
          params: {
            item_type: itemType,
            item_id: itemId,
            item_source: itemSource
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setIsFavorite(false);
        toast.success("Removed from favorites");
        if (onToggle) onToggle(false, itemId);
      } else {
        // Add to favorites
        await axios.post(`${API}/api/favorites`, {
          item_type: itemType,
          item_id: itemId,
          item_title: itemTitle,
          item_source: itemSource,
          item_data: itemData,
          tags: tags
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setIsFavorite(true);
        toast.success("Added to favorites");
        if (onToggle) onToggle(true, itemId);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("Already in favorites");
        setIsFavorite(true);
      } else {
        toast.error("Failed to update favorites");
      }
      console.error("Favorite toggle error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={toggleFavorite}
      disabled={loading}
      className={`${className} ${isFavorite ? 'text-red-500 hover:text-red-600' : 'text-gray-400 hover:text-red-500'} transition-colors`}
      title={isFavorite ? "Remove from favorites" : "Add to favorites"}
    >
      <Heart 
        className={`${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} ${isFavorite ? 'fill-current' : ''}`} 
      />
      {showText && (
        <span className="ml-2">
          {isFavorite ? 'Favorited' : 'Favorite'}
        </span>
      )}
    </Button>
  );
}